// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll from "@tps/App/Components/Common/FancyScroll";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // XNG-Change:
    filtersWrapper?: InjectableCss;
    fancyScrollStyles?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const accordionItem = React.createRef<HTMLDivElement>();
    // <xng-change>
    const [expanded, setExpanded] = useState(false);
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const search = useAppSelector(state => getLocation(state).search);
    const [expandedState, setExpandedState] = useState(expandByDefault);
    const anySelected = facets.find(f => f.selected) !== undefined;

    useEffect(() => {
        if (!search) {
            setExpandedState(expandByDefault);
        }
    }, [search]);
    // </xng-change>

    return (
        // xng-change: replace ManagedAccordionSection with AccordionSection
        <AccordionSection
            title={title}
            {...styles.accordionSection}
            expanded={expandedState || anySelected}
            onTogglePanel={() => {
                setExpandedState(!expandedState);
            }}
        >
            {/* XNG-Change add fancy scroll */}
            <FancyScroll refElement={accordionItem} extendedStyles={styles.fancyScrollStyles}>
                <StyledWrapper ref={accordionItem} {...styles.filtersWrapper}>
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {facet.name}&nbsp;&nbsp;&nbsp;{facet.count !== -1 && `(${facet.count})`}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link {...styles.seeLessLink} onClick={() => setExpanded(false)} aria-label="See Less">
                                {translate("Load Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLink} onClick={() => setExpanded(true)} aria-label="See All">
                                {translate("Load More")}
                            </Link>
                        ))}
                </StyledWrapper>
            </FancyScroll>
        </AccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
